<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ business ? 'Edit Business' : 'New Business' }}
    </h2>

    <form @submit.prevent="handleSubmit" class="space-y-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiControl id="business-name" label="Name *">
          <UiInput v-model="form.name" placeholder="Business Name" />
        </UiControl>
        <UiControl id="business-nickname" label="Nickname">
          <UiInput v-model="form.nickname" placeholder="Short name" />
        </UiControl>
        <UiControl id="business-category" label="Category">
          <UiFormAsyncSelect
            v-model="form.category"
            label="label"
            value-prop="value"
            :async-source="categoriesSource"
            size="sm" />
        </UiControl>
        <UiControl id="business-phone" label="Phone">
          <UiInput v-model="form.phone_number" placeholder="(555) 123-4567" />
        </UiControl>
        <UiControl id="business-email" label="Email">
          <UiInput v-model="form.email" placeholder="info@example.com" />
        </UiControl>
        <UiControl id="business-website" label="Website URL">
          <UiInput v-model="form.website_url" placeholder="https://example.com" />
        </UiControl>
      </div>

      <UiControl id="business-description" label="Description">
        <UiTextarea v-model="form.description" />
      </UiControl>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Address
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="md:col-span-2">
          <UiControl id="business-street" label="Street Address">
            <UiInput v-model="form.street_address" placeholder="123 Main St" />
          </UiControl>
        </div>
        <UiControl id="business-city" label="City">
          <UiInput v-model="form.city" placeholder="City" />
        </UiControl>
        <UiControl id="business-state" label="State">
          <UiInput v-model="form.state" placeholder="State" />
        </UiControl>
        <UiControl id="business-zip" label="ZIP">
          <UiInput v-model="form.zip" placeholder="12345" />
        </UiControl>
        <UiControl id="business-country" label="Country">
          <UiInput v-model="form.country" placeholder="US" />
        </UiControl>
      </div>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Social Links
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiControl id="business-facebook" label="Facebook">
          <UiInput v-model="form.facebook_url" placeholder="https://facebook.com/..." />
        </UiControl>
        <UiControl id="business-instagram" label="Instagram">
          <UiInput v-model="form.instagram_url" placeholder="https://instagram.com/..." />
        </UiControl>
        <UiControl id="business-twitter" label="Twitter">
          <UiInput v-model="form.twitter_url" placeholder="https://twitter.com/..." />
        </UiControl>
        <UiControl id="business-youtube" label="YouTube">
          <UiInput v-model="form.youtube_url" placeholder="https://youtube.com/..." />
        </UiControl>
        <UiControl id="business-linkedin" label="LinkedIn">
          <UiInput v-model="form.linkedin_url" placeholder="https://linkedin.com/..." />
        </UiControl>
        <UiControl id="business-tiktok" label="TikTok">
          <UiInput v-model="form.tiktok_url" placeholder="https://tiktok.com/..." />
        </UiControl>
      </div>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Brand Style Guide
      </h3>
      <UiControl id="business-logo" label="Logo">
        <div class="flex items-center gap-4">
          <div v-if="logoPreview || business?.logo_url" class="w-16 h-16 rounded-lg border-2 border-gray-200 dark:border-gray-600 overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-800">
            <img :src="logoPreview || business?.logo_url" class="max-w-full max-h-full object-contain" />
          </div>
          <div>
            <label class="btn btn-secondary btn-sm cursor-pointer">
              {{ logoPreview || business?.logo_url ? 'Change Logo' : 'Upload Logo' }}
              <input type="file" accept="image/*" class="hidden" @change="handleLogoSelect" />
            </label>
            <p class="text-xs text-gray-500 mt-1">PNG or JPG, recommended 200x200px or larger</p>
          </div>
        </div>
      </UiControl>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiControl id="business-color-primary" label="Primary Color">
          <div class="flex gap-2 items-center">
            <input v-model="form.brand_colors.primary" type="color" class="w-10 h-10 rounded cursor-pointer border-2 border-gray-200 dark:border-gray-600">
            <UiInput v-model="form.brand_colors.primary" placeholder="#000000" />
          </div>
        </UiControl>
        <UiControl id="business-color-secondary" label="Secondary Color">
          <div class="flex gap-2 items-center">
            <input v-model="form.brand_colors.secondary" type="color" class="w-10 h-10 rounded cursor-pointer border-2 border-gray-200 dark:border-gray-600">
            <UiInput v-model="form.brand_colors.secondary" placeholder="#333333" />
          </div>
        </UiControl>
        <UiControl id="business-color-accent" label="Accent Color">
          <div class="flex gap-2 items-center">
            <input v-model="form.brand_colors.accent" type="color" class="w-10 h-10 rounded cursor-pointer border-2 border-gray-200 dark:border-gray-600">
            <UiInput v-model="form.brand_colors.accent" placeholder="#0066CC" />
          </div>
        </UiControl>
        <UiControl id="business-color-text" label="Text Color">
          <div class="flex gap-2 items-center">
            <input v-model="form.brand_colors.text" type="color" class="w-10 h-10 rounded cursor-pointer border-2 border-gray-200 dark:border-gray-600">
            <UiInput v-model="form.brand_colors.text" placeholder="#FFFFFF" />
          </div>
        </UiControl>
        <UiControl id="business-font-heading" label="Heading Font">
          <UiInput v-model="form.brand_fonts.heading" placeholder="Arial" />
        </UiControl>
        <UiControl id="business-font-body" label="Body Font">
          <UiInput v-model="form.brand_fonts.body" placeholder="Arial" />
        </UiControl>
      </div>
      <UiControl id="business-style-notes" label="Style Guide Notes">
        <UiTextarea v-model="form.style_guide_notes" placeholder="Additional brand guidelines..." />
      </UiControl>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Brand Voice
      </h3>
      <UiControl id="business-brand-voice" label="Brand Voice & Tone">
        <UiTextarea
          v-model="form.brand_voice"
          placeholder="Describe this brand's voice and tone. E.g.: Friendly and approachable. Use short sentences. Speak directly to homeowners. Avoid jargon. Emphasize trust and reliability."
          rows="5" />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Used by AI when writing and optimizing content for this business.
        </p>
      </UiControl>

      <div v-if="errors" class="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ errors }}
        </p>
      </div>

      <div class="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
        <button
          type="button"
          @click="$emit('on-close-panel')"
          class="btn btn-secondary btn-sm">
          Cancel
        </button>
        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm">
          {{ saving ? 'Saving...' : (business ? 'Update' : 'Create') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { createBusiness, updateBusiness, getGbpCategories } from '~/javascript/api/businesses.js'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiTextarea from '~/javascript/components/ui/form/textarea/textarea.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import { UiFormAsyncSelect } from '~/javascript/components/ui/form/async_select'

const props = defineProps({
  business: { type: Object, default: null }
})

const emit = defineEmits(['on-close-panel', 'saved'])

const saving = ref(false)
const errors = ref(null)
const logoFile = ref(null)
const logoPreview = ref(null)

function handleLogoSelect(event) {
  const file = event.target.files[0]
  if (!file) return
  logoFile.value = file
  logoPreview.value = URL.createObjectURL(file)
}

const categoriesSource = () => getGbpCategories().then((res) => res.data.categories)

const defaultForm = {
  name: '',
  nickname: '',
  category: '',
  description: '',
  phone_number: '',
  email: '',
  website_url: '',
  street_address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  facebook_url: '',
  instagram_url: '',
  twitter_url: '',
  youtube_url: '',
  linkedin_url: '',
  tiktok_url: '',
  brand_colors: { primary: '', secondary: '', accent: '', text: '' },
  brand_fonts: { heading: '', body: '' },
  style_guide_notes: '',
  brand_voice: ''
}

const form = ref({ ...defaultForm })

function buildPayload() {
  if (!logoFile.value) return { business: form.value }

  const fd = new FormData()
  Object.entries(form.value).forEach(([key, val]) => {
    if (typeof val === 'object' && val !== null) {
      Object.entries(val).forEach(([subKey, subVal]) => {
        fd.append(`business[${key}][${subKey}]`, subVal || '')
      })
    } else {
      fd.append(`business[${key}]`, val || '')
    }
  })
  fd.append('business[logo]', logoFile.value)
  return fd
}

const handleSubmit = async() => {
  try {
    saving.value = true
    errors.value = null
    const payload = buildPayload()

    if (props.business) {
      await updateBusiness(props.business.id, payload)
    } else {
      await createBusiness(payload)
    }
    emit('saved')
  } catch (error) {
    if (error.response?.data?.details) {
      errors.value = Object.values(error.response.data.details).flat().join(', ')
    } else {
      errors.value = 'An error occurred. Please try again.'
    }
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (props.business) {
    Object.keys(defaultForm).forEach((key) => {
      if (key === 'brand_colors') {
        form.value[key] = { ...defaultForm.brand_colors, ...(props.business[key] || {}) }
      } else if (key === 'brand_fonts') {
        form.value[key] = { ...defaultForm.brand_fonts, ...(props.business[key] || {}) }
      } else {
        form.value[key] = props.business[key] || ''
      }
    })
  }
})
</script>
